import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import Starfield from "@/components/Starfield";
import { getProject, PROJECTS, ACCENTS } from "@/data/projects";
import { candleFlicker } from "@/components/Soundscape";

const EASE = [0.16, 1, 0.3, 1];

/* chapter heading — left-aligned, engraved */
const ChapterHeading = ({ children, testid }) => (
  <div className="text-left">
    <h2
      data-testid={testid}
      className="font-serif-engraved gold-engraved-deep text-3xl font-semibold uppercase tracking-[0.12em] md:text-4xl"
    >
      {children}
    </h2>
    <div className="mt-4 h-px w-28 bg-gradient-to-r from-[#c9a227]/80 to-transparent" />
  </div>
);

/* one work within a realm — its lockup, its line, its souls */
const WorkChapter = ({ work }) => (
  <motion.div
    key={work.id}
    data-testid={`work-${work.id}`}
    initial={{ opacity: 0, y: 26 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, ease: EASE }}
    className="flex flex-col items-center text-center"
  >
    {work.titleImg ? (
      <img
        src={work.titleImg}
        alt={work.title}
        data-testid="work-title-logo"
        className="w-full max-w-sm md:max-w-xl"
      />
    ) : (
      <h3
        data-testid="work-title-text"
        className="font-serif-engraved gold-engraved text-4xl font-semibold uppercase leading-tight tracking-[0.1em] md:text-6xl"
      >
        {work.title}
      </h3>
    )}
    <p className="mt-4 text-[10px] uppercase tracking-[0.4em] text-[#c9a227]/60">{work.medium}</p>

    <motion.section
      data-testid="project-logline-section"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, ease: EASE }}
      className="mt-14 w-full"
    >
      <p
        data-testid="project-logline"
        className="font-serif-engraved mx-auto max-w-3xl text-center text-xl font-light italic leading-relaxed text-[#fdfbf7]/80 md:text-2xl"
      >
        “{work.logline}”
      </p>
    </motion.section>

    {work.credits && (
      <motion.section
        data-testid="project-cast-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1 }}
        className="mt-28 w-full"
      >
        <ChapterHeading testid="cast-crew-heading">Cast and Crew</ChapterHeading>

        {/* the crew */}
        <div data-testid="project-crew-section" className="mt-12 space-y-4 text-left">
          {work.credits.crew.map((c) => (
            <p
              key={c.name}
              data-testid={`crew-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="leading-relaxed"
            >
              <span className="font-serif-engraved text-xl text-[#fdfbf7]/85">{c.name}</span>
              <span className="font-serif-engraved px-2 italic text-[#c9a227]/60">—</span>
              <span className="font-serif-engraved text-xl italic text-[#e8c95a]/90">{c.role}</span>
            </p>
          ))}
        </div>

        {/* the cast rail */}
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {work.credits.cast.map((c, i) => (
            <motion.div
              key={c.role}
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: EASE }}
              data-testid={`cast-card-${c.role.toLowerCase().replace(/\s+/g, "-")}`}
              className="deco-frame group flex items-center gap-5 border border-[#c9a227]/20 bg-black/40 p-6 text-left transition-shadow duration-500 hover:shadow-[0_0_35px_rgba(201,162,39,0.15)]"
            >
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-[#c9a227]/40 bg-[#c9a227]/5 transition-transform duration-500 group-hover:scale-105">
                <span className="font-serif-engraved text-xl text-[#e8c95a]">
                  {c.actor.split(" ").map((w) => w[0]).join("")}
                </span>
              </div>
              <div>
                {c.lead && (
                  <p className="text-[9px] uppercase tracking-[0.4em] text-[#c9a227]/60">✦ Lead ✦</p>
                )}
                <p className="font-serif-engraved mt-1 text-xl text-[#e8c95a]">{c.actor}</p>
                <p className="mt-1 text-sm font-light italic tracking-wide text-[#fdfbf7]/60">
                  as {c.role}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>
    )}
  </motion.div>
);

const ProjectPage = () => {
  const { slug } = useParams();
  const project = getProject(slug);
  const accent = ACCENTS[slug] || ACCENTS.films;
  const index = PROJECTS.findIndex((p) => p.slug === slug);
  const [workId, setWorkId] = useState(null);

  useEffect(() => {
    setWorkId(null);
    const t = setTimeout(() => candleFlicker(), 900);
    return () => clearTimeout(t);
  }, [index]);

  if (!project) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-6 bg-[#060b07] px-6 text-center" data-testid="project-not-found">
        <p className="font-serif-engraved text-4xl text-[#fdfbf7]/80">This story is sealed.</p>
        <Link to="/" data-testid="not-found-home-link" className="text-[11px] uppercase tracking-[0.3em] text-[#c9a227] hover:text-[#e8c95a]">
          Return to the archive
        </Link>
      </main>
    );
  }

  const work = project.works.find((w) => w.id === workId) || project.works[0];

  return (
    <main
      data-testid={`project-page-${project.slug}`}
      className="relative min-h-screen overflow-hidden bg-[#060b07]"
    >
      {/* the realm's world — the background of the whole page */}
      <div className="pointer-events-none fixed inset-0" aria-hidden="true">
        <div className="animate-kenburns h-full w-full">
          <img
            src={project.keyArt}
            alt=""
            className="h-full w-full object-cover opacity-75 md:hidden"
            style={{ objectPosition: project.artPosMobile || project.artPos || "50% 50%" }}
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
          <img
            src={project.keyArt}
            alt=""
            className="hidden h-full w-full object-cover opacity-75 md:block"
            style={{ objectPosition: project.artPos || "50% 50%" }}
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        </div>
        <div className="absolute inset-0 bg-black/35" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060b07]/80 via-[#060b07]/25 to-[#060b07]" />
        <motion.div
          className="absolute inset-0"
          animate={{ opacity: [0.15, 0.35, 0.15] }}
          transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
          style={{ background: `radial-gradient(ellipse 55% 45% at 50% 55%, ${accent.halo}, transparent 70%)` }}
        />
      </div>
      <div className="pointer-events-none fixed inset-0" aria-hidden="true">
        <Starfield density={45} />
      </div>

      {/* the way home — logo, always */}
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 1 }}
        className="absolute inset-x-0 top-5 z-30 flex justify-center md:top-6"
      >
        <Link to="/" data-testid="project-logo-home-link" aria-label="Return to the constellation">
          <img
            src="/assets/logo_transparent.webp"
            alt="Celestial Studios"
            className="h-28 w-auto opacity-90 transition-opacity duration-300 hover:opacity-100 md:h-36"
          />
        </Link>
      </motion.div>

      {/* back to the sky */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9, duration: 1 }}
        className="absolute left-6 top-6 z-40 md:left-10 md:top-9"
      >
        <Link
          to="/"
          data-testid="project-back-link"
          className="group inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#fdfbf7]/50 transition-colors duration-300 hover:text-[#e8c95a]"
        >
          <ArrowLeft size={14} strokeWidth={1.5} className="transition-transform duration-500 group-hover:-translate-x-1" />
          All stories
        </Link>
      </motion.div>

      {/* the realm's title, towering over the world */}
      <section className="relative z-10 flex min-h-[100svh] flex-col items-center justify-center px-6 text-center">
        <motion.h1
          data-testid="project-title-logo"
          initial={{ opacity: 0, scale: 1.06, filter: "blur(10px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ delay: 0.3, duration: 1.1, ease: EASE }}
          className="font-serif-engraved gold-engraved text-6xl font-semibold uppercase leading-none tracking-[0.12em] sm:text-7xl md:text-8xl"
        >
          {project.title}
        </motion.h1>
        <motion.span
          data-testid="project-medium-pill"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8, ease: EASE }}
          className="mt-8 inline-block rounded-full border border-[#c9a227]/45 bg-black/45 px-7 py-2.5 text-[10px] uppercase tracking-[0.4em] text-[#e8c95a] backdrop-blur-md"
        >
          {project.medium}
        </motion.span>
      </section>

      {/* the works of this realm — each behind its own tab */}
      <div data-testid="volume-tabs" className="relative z-10 -mt-14 flex justify-center px-6 pb-12">
        <div className="inline-flex max-w-full items-center gap-1 overflow-x-auto rounded-full border border-[#c9a227]/25 bg-black/55 p-1.5 backdrop-blur-md">
          {project.works.map((w) => (
            <button
              key={w.id}
              data-testid={`tab-${w.id}`}
              onClick={() => setWorkId(w.id)}
              className={`shrink-0 rounded-full px-6 py-2.5 text-[10px] uppercase tracking-[0.24em] transition-all duration-500 ${
                work.id === w.id
                  ? "bg-[#c9a227]/25 text-[#e8c95a] shadow-[0_0_20px_rgba(201,162,39,0.25)]"
                  : "text-[#fdfbf7]/45 hover:text-[#fdfbf7]/80"
              }`}
            >
              {w.title}
            </button>
          ))}
        </div>
      </div>

      {/* the chapters — over a seamless descent into black */}
      <div className="relative z-10 bg-gradient-to-b from-transparent via-[#060b07]/85 to-[#060b07] px-6 pb-28 pt-10 md:px-10">
        <div className="mx-auto w-full max-w-6xl">
          <WorkChapter key={work.id} work={work} />
        </div>
      </div>
    </main>
  );
};

export default ProjectPage;
